import { ROADMAP } from "@/constants/testIds/roadmap";
import { ArrowRight, Sparkles } from "lucide-react";
import { motion } from "framer-motion";

export default function Hero() {
  const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <section
      id="top"
      data-testid={ROADMAP.hero}
      className="relative pt-28 lg:pt-36 pb-20 lg:pb-28 border-b border-[#DEE2E6] bg-white overflow-hidden"
    >
      {/* technical grid background */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(#0A0A0A 1px, transparent 1px), linear-gradient(90deg, #0A0A0A 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 border border-[#DEE2E6] bg-white px-3 py-1.5 text-[11px] uppercase tracking-[0.15em] text-[#495057] mb-6"
          >
            <Sparkles size={12} className="text-[#FF4F00]" />
            Implementation Roadmap · Gigmann Medicals · v1.0
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold tracking-tighter leading-[0.95] text-[#0A0A0A]"
          >
            Gr8Boost ERP
            <span className="text-[#FF4F00]">.</span>
            <br />
            <span className="text-[#495057]">The white-label ERP,</span>
            <br />
            powered by Gr8Agentic AI.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="mt-8 max-w-2xl text-lg text-[#495057] leading-relaxed"
          >
            One unified cloud ERP for medical distribution, engineering services, and operations —
            with an AI co-pilot that answers questions, drafts documents, predicts shortages, and
            catches anomalies before they cost you.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="mt-10 flex flex-wrap gap-3"
          >
            <button
              onClick={() => scrollTo("phases")}
              data-testid={ROADMAP.heroPrimaryCta}
              className="group inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#0033A0] text-white font-medium px-6 py-3.5 rounded-sm transition-colors duration-200"
            >
              Explore the 6-phase roadmap
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-200" />
            </button>
            <button
              onClick={() => scrollTo("gr8agentic")}
              data-testid={ROADMAP.heroSecondaryCta}
              className="inline-flex items-center gap-2 border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white text-[#0A0A0A] font-medium px-6 py-3.5 rounded-sm transition-colors duration-200"
            >
              Meet Gr8Agentic AI
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-12 grid grid-cols-3 gap-0 border-t border-[#DEE2E6]"
          >
            {[
              { k: "8", v: "Functional modules" },
              { k: "6", v: "Implementation phases" },
              { k: "12+", v: "KPIs tracked live" },
            ].map((s, i) => (
              <div
                key={s.v}
                className={`py-5 px-1 ${i !== 0 ? "border-l border-[#DEE2E6] pl-6" : ""}`}
              >
                <div className="font-heading text-4xl font-bold tracking-tighter text-[#0A0A0A]">
                  {s.k}
                </div>
                <div className="text-xs uppercase tracking-[0.15em] text-[#495057] mt-1">
                  {s.v}
                </div>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="lg:col-span-5"
        >
          <div className="relative border border-[#0A0A0A] bg-[#F8F9FA]">
            <div className="flex items-center justify-between px-4 py-2 border-b border-[#DEE2E6] bg-white">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#D90429]" />
                <span className="w-2 h-2 rounded-full bg-[#F5B700]" />
                <span className="w-2 h-2 rounded-full bg-[#0B7A75]" />
              </div>
              <span className="text-[10px] uppercase tracking-[0.15em] text-[#495057]">
                gr8agentic.gr8boost.app
              </span>
            </div>
            <div className="p-5 space-y-3 font-mono text-xs">
              <div className="flex gap-2">
                <span className="text-[#0033A0] font-bold">▸</span>
                <span className="text-[#495057]">
                  Show me overdue invoices &gt; 60 days, grouped by sales rep.
                </span>
              </div>
              <div className="border border-[#DEE2E6] bg-white p-3 space-y-1.5">
                <div className="flex justify-between text-[11px]">
                  <span className="text-[#495057]">K. Mensah</span>
                  <span className="font-bold text-[#D90429]">GHS 142,300</span>
                </div>
                <div className="flex justify-between text-[11px]">
                  <span className="text-[#495057]">A. Owusu</span>
                  <span className="font-bold text-[#F5B700]">GHS 98,750</span>
                </div>
                <div className="flex justify-between text-[11px]">
                  <span className="text-[#495057]">N. Asante</span>
                  <span className="font-bold text-[#0B7A75]">GHS 41,200</span>
                </div>
              </div>
              <div className="flex gap-2 text-[11px] text-[#0A0A0A]">
                <Sparkles size={12} className="text-[#FF4F00] flex-shrink-0 mt-0.5" />
                <span>
                  Gr8Agentic AI drafted 3 follow-up emails. Approve to send →
                </span>
              </div>
            </div>
          </div>
          <div className="mt-3 text-[10px] uppercase tracking-[0.15em] text-[#495057] text-right">
            Gr8Agentic AI · Live demo preview
          </div>
        </motion.div>
      </div>
    </section>
  );
}
